// Contracts — kids cabinet CHECK-IN (staff) (2026-06-28). List today's meetings,
// mark a family arrived, add walk-ins. Shapes snapped from migrations/0002 +
// the old kids-worker routes/checkin.ts.
import { z } from 'zod';
import { KidsGenderSchema } from '../kids';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Child name parts: latin letters + space/apostrophe/hyphen, must start with a
// letter. Mirrors NAME_RE in kids/session.ts + kids/apply.ts so a walk-in child
// normalizes to the SAME "First Last" the packer matches on (R2 upload folder /
// join key). Walk-in previously stored a single free `name`; splitting forces
// the staff-entered name into the same canonical shape as booked/Session-V1 kids.
const NAME_RE = /^[A-Za-z][A-Za-z '\-]*$/;

// GET /api/kids/checkin/today?office=&date= → meetings (+children) for a date.
// office optional: falls back to the staff member's bound office.
export const KidsCheckinTodayQuerySchema = z.object({
  office: z.string().trim().max(60).optional(),
  date: z.string().regex(DATE_RE).optional(),
});
export type KidsCheckinTodayQuery = z.infer<typeof KidsCheckinTodayQuerySchema>;

export const KidsCheckinChildSchema = z.object({
  id: z.string(),
  meetingId: z.string().nullable(),
  name: z.string(),
  age: z.number().int().nullable(),
  time: z.string().nullable(),
});

export const KidsCheckinMeetingSchema = z.object({
  meetingId: z.string(),
  status: z.string(),
  time: z.string(),
  parentName: z.string(),
  parentPhone: z.string().nullable(),
  parentEmail: z.string(),
  children: z.array(KidsCheckinChildSchema),
});

export const KidsCheckinTodayResSchema = z.object({
  date: z.string(),
  meetings: z.array(KidsCheckinMeetingSchema),
});
export type KidsCheckinTodayRes = z.infer<typeof KidsCheckinTodayResSchema>;

// GET /api/kids/checkin/pending?office=&q= → «старые заявки»: лендинг-заявки
// прошлых дней без прихода (source='landing', status='scheduled', date<today).
// Открывается тоглом на чекин-борде; q ищет по имени родителя ИЛИ ребёнка.
export const KidsCheckinPendingQuerySchema = z.object({
  office: z.string().trim().max(60).optional(), // manager only; staff pinned to own
  q: z.string().trim().max(120).optional(),
});
export type KidsCheckinPendingQuery = z.infer<typeof KidsCheckinPendingQuerySchema>;

// Same card as the board + the submit date (staff sees how old the request is).
export const KidsCheckinPendingMeetingSchema = KidsCheckinMeetingSchema.extend({
  date: z.string(),
});
export const KidsCheckinPendingResSchema = z.object({
  meetings: z.array(KidsCheckinPendingMeetingSchema),
});
export type KidsCheckinPendingRes = z.infer<typeof KidsCheckinPendingResSchema>;

// POST /api/kids/checkin/:id/check-in → mark meeting checked_in (no body).
export const KidsCheckinAckResSchema = z.object({ ok: z.literal(true) });
export type KidsCheckinAckRes = z.infer<typeof KidsCheckinAckResSchema>;

// POST /api/kids/checkin/walk-in → create parent + checked_in meeting + children.
export const KidsWalkInReqSchema = z.object({
  parent: z.object({
    name: z.string().trim().min(1).max(200),
    phone: z.string().trim().max(30).optional(),
    email: z.string().trim().regex(EMAIL_RE).max(200).optional(),
  }),
  children: z
    .array(
      z.object({
        // Two latin-only parts (not one free field): the worker normalizes them
        // into the single `children.name` D1 column as "First Last".
        firstName: z.string().trim().min(1).max(60).regex(NAME_RE),
        lastName: z.string().trim().min(1).max(60).regex(NAME_RE),
        age: z.number().int().min(5).max(17).optional(),
        gender: KidsGenderSchema.optional(),
        // Walk-in 2.0 (Tim 20.07): full participant profile — same per-child
        // shape as the moderator «Новая сессия» form (kids/session.ts), so a
        // walk-in child arrives shoot-ready (appearance + photo + faceval).
        // ALL optional at the contract level: the reception walk-in form still
        // sends only name/age/gender (deliberately untouched); the CHECK-IN
        // form enforces requiredness client-side, exactly like SessionCreate.
        ethnicity: z.string().trim().max(120).optional(),
        skinTone: z.number().int().min(1).max(10).optional(),
        hasBeard: z.boolean().optional(),
        hasMustache: z.boolean().optional(),
        usesReadingGlasses: z.boolean().optional(),
        accessoryName: z.string().trim().max(120).optional(),
        // Downscaled JPEG dataURL (mirrors kids/session.ts). PII — never logged.
        photo: z.string().max(2_000_000).optional(),
        // faceval validate-job id → the worker auto-adds the face to the base.
        facevalJobId: z.string().optional(),
      }),
    )
    .min(1)
    .max(20),
});
export type KidsWalkInReq = z.infer<typeof KidsWalkInReqSchema>;

export const KidsWalkInResSchema = z.object({ meetingId: z.string() });
export type KidsWalkInRes = z.infer<typeof KidsWalkInResSchema>;

// ── reception board (staff): set meeting status + unbook a child ──────────────
// POST /api/kids/checkin/:id/status → reception sets a meeting's status.
// The full meeting-status union lives in ../kids/enums (KidsMeetingStatusSchema);
// this Req deliberately RESTRICTS the reception-settable values to a whitelist:
// `done` is DERIVED by the shot flow (F1) and must NOT be settable here.
export const KidsSetMeetingStatusReqSchema = z.object({
  status: z.enum(['scheduled', 'checked_in', 'canceled', 'rejected']),
});
export type KidsSetMeetingStatusReq = z.infer<typeof KidsSetMeetingStatusReqSchema>;

export const KidsSetMeetingStatusResSchema = z.object({
  ok: z.literal(true),
  meetingId: z.string(),
  status: z.enum(['scheduled', 'checked_in', 'canceled', 'rejected']),
});
export type KidsSetMeetingStatusRes = z.infer<typeof KidsSetMeetingStatusResSchema>;

// POST /api/kids/children/:id/unbook → reception removes a booked child that has
// NO recorded (started/done) session. childId is a path param → no request body.
export const KidsRemoveChildResSchema = z.object({
  ok: z.literal(true),
  childId: z.string(),
});
export type KidsRemoveChildRes = z.infer<typeof KidsRemoveChildResSchema>;
